'use client';

/**
 * Login page — email + password authentication via Better Auth.
 *
 * Features:
 * - Email/password form with server-side credential validation
 * - Session expired notification when redirected from middleware.ts
 * - Callback URL support for post-login redirect
 * - Link to SSO login for organisation-federated users
 *
 * Polish pass 1305 — display-tier H1, 44px touch-target inputs, stronger
 * focus ring (2px + offset), CTA elevated with shadow + active state.
 */

import { Suspense, useState, useTransition } from 'react';
import { useSearchParams } from 'next/navigation';
import { Loader2 } from 'lucide-react';
import { authClient } from '@/lib/auth/client';
import { authReturnPath } from '@/lib/auth/return-path';

export default function LoginPage() {
  return (
    <Suspense fallback={<div className="text-center text-muted-foreground">Loading…</div>}>
      <LoginForm />
    </Suspense>
  );
}

function LoginForm() {
  const searchParams = useSearchParams();
  const callbackUrl = authReturnPath(searchParams.get('callbackUrl'));
  const expired = searchParams.get('expired') === 'true';
  const resetSuccess = searchParams.get('reset') === 'success';

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [isPending, startTransition] = useTransition();

  function handlePasswordChange(value: string) {
    setPassword(value);
    setPasswordError(null);
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);

    startTransition(async () => {
      const result = await authClient.signIn.email({
        email: email.toLowerCase().trim(),
        password,
        callbackURL: callbackUrl,
      });

      if (result.error) {
        setError(
          result.error.message || 'Invalid email or password. Please try again.',
        );
      }
    });
  }

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-3xl font-bold tracking-tight text-foreground">
          Welcome back
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Sign in to continue with your caseload.
        </p>
      </div>

      {expired && (
        <div className="mb-5 rounded-lg border border-[var(--status-warning-text)] bg-[var(--status-warning-bg)] px-4 py-3 text-sm text-[var(--status-warning-text)]">
          Your session has expired. Please sign in again.
        </div>
      )}

      {resetSuccess && (
        <div className="mb-5 rounded-lg border border-[var(--status-success-text)] bg-[var(--status-success-bg)] px-4 py-3 text-sm text-[var(--status-success-text)]">
          Password reset successful. Sign in with your new password.
        </div>
      )}

      {error && (
        <div
          role="alert"
          className="mb-5 rounded-lg border border-[var(--status-danger-text)] bg-[var(--status-danger-bg)] px-4 py-3 text-sm text-[var(--status-danger-text)]"
        >
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-5">
        <div>
          <label
            htmlFor="email"
            className="mb-1.5 block text-sm font-semibold text-foreground"
          >
            Email
          </label>
          <input
            id="email"
            type="email"
            required
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="block h-11 w-full rounded-lg border border-border bg-card px-4 text-base text-foreground placeholder:text-muted-foreground transition-shadow focus:border-[var(--brand-primary)] focus:outline-none focus:ring-2 focus:ring-[var(--brand-primary)] focus:ring-offset-2 focus:ring-offset-[var(--card)]"
            placeholder="you@example.com"
          />
        </div>

        <div>
          <div className="mb-1.5 flex items-baseline justify-between">
            <label
              htmlFor="password"
              className="block text-sm font-semibold text-foreground"
            >
              Password
            </label>
            <a
              href="/auth/forgot-password"
              className="text-xs font-semibold text-[var(--brand-primary)] hover:underline"
            >
              Forgot password?
            </a>
          </div>
          <input
            id="password"
            type="password"
            required
            autoComplete="current-password"
            value={password}
            onChange={(e) => handlePasswordChange(e.target.value)}
            className={`block h-11 w-full rounded-lg border bg-card px-4 text-base text-foreground placeholder:text-muted-foreground transition-shadow focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-[var(--card)] ${
              passwordError
                ? 'border-[var(--status-danger-text)] focus:border-[var(--status-danger-text)] focus:ring-[var(--status-danger-text)]'
                : 'border-border focus:border-[var(--brand-primary)] focus:ring-[var(--brand-primary)]'
            }`}
            placeholder="Enter your password"
            aria-invalid={!!passwordError}
            aria-describedby={passwordError ? 'login-password-error' : undefined}
          />
          {passwordError && (
            <p
              id="login-password-error"
              className="mt-1.5 text-xs font-semibold text-[var(--status-danger-text)]"
            >
              {passwordError}
            </p>
          )}
        </div>

        <button
          type="submit"
          disabled={isPending}
          className="group flex h-11 w-full items-center justify-center gap-2 rounded-lg bg-[var(--brand-primary)] px-6 text-base font-semibold text-[var(--brand-primary-foreground)] shadow-sm transition-all duration-150 hover:bg-[var(--brand-primary-hover)] hover:shadow-md active:scale-[0.99] focus:outline-none focus:ring-2 focus:ring-[var(--brand-primary)] focus:ring-offset-2 focus:ring-offset-[var(--card)] disabled:cursor-not-allowed disabled:opacity-60"
        >
          {isPending ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
              Signing in…
            </>
          ) : (
            <>
              Sign in
              <span
                className="transition-transform group-hover:translate-x-0.5"
                aria-hidden="true"
              >
                &rarr;
              </span>
            </>
          )}
        </button>
      </form>

      <div className="relative my-6">
        <div className="absolute inset-0 flex items-center" aria-hidden>
          <span className="w-full border-t border-border" />
        </div>
        <div className="relative flex justify-center">
          <span className="bg-card px-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            or
          </span>
        </div>
      </div>

      <a
        href="/auth/sso"
        className="flex h-11 w-full items-center justify-center rounded-lg border border-border bg-card px-6 text-sm font-semibold text-foreground transition-colors hover:bg-[var(--brand-primary-light)] focus:outline-none focus:ring-2 focus:ring-[var(--brand-primary)] focus:ring-offset-2 focus:ring-offset-[var(--card)]"
      >
        Sign in with your organisation (SSO)
      </a>

      <p className="mt-6 text-center text-xs text-muted-foreground">
        Need access? Contact your organisation administrator to be invited.
      </p>
    </div>
  );
}
